import { useEffect, useMemo, useState } from 'react'
import { apiGet, apiPost } from '../utils/api'
import { useI18n } from '../i18n'

type Agent = {
  id: number
  tenant_id: number
  endpoint_id: number
  name: string
  version: string
  os: string
  arch: string
  state: string
  paused: boolean
  exec_simulate: boolean
  last_seen?: string
}

export default function Agents() {
  const { t } = useI18n()
  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    (async () => {
      try {
        const data = await apiGet('/agents')
        setAgents(data)
      } catch (e: any) {
        setError(e?.message || t('agents.errors.load'))
      } finally {
        setLoading(false)
      }
    })()
  }, [t])

  const rows = useMemo(() => agents, [agents])

  const genInstaller = async (endpointId: number) => {
    try {
      const res = await fetch('/installers/linux-systemd', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint_id: endpointId, expires_minutes: 30 })
      })
      if (!res.ok) throw new Error(t('agents.errors.installer'))
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'install-backup-agent.sh'
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e: any) {
      alert(e?.message || t('agents.errors.installer'))
    }
  }
  const genDocker = async (endpointId: number) => {
    try {
      const res = await fetch('/installers/docker-run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint_id: endpointId, expires_minutes: 30 })
      })
      if (!res.ok) throw new Error(t('agents.errors.docker'))
      const text = await res.text()
      await navigator.clipboard.writeText(text)
      alert(t('agents.feedback.docker_copy'))
    } catch (e: any) { alert(e?.message || t('agents.errors.docker')) }
  }
  const rotateKey = async (ag: Agent) => {
    if (!confirm(t('agents.confirm.rotate', { id: ag.id }))) return
    const res = await apiPost(`/agents/${ag.id}/rotate-key`, {})
    alert(`${t('agents.feedback.new_key')}: ${res.agent_key || t('agents.feedback.key_hint')}`)
  }
  const deleteAgent = async (ag: Agent) => {
    if (!confirm(t('agents.confirm.delete', { id: ag.id }))) return
    await fetch(`/agents/${ag.id}`, { method: 'DELETE' })
    setAgents(prev => prev.filter(x => x.id !== ag.id))
  }

  const togglePause = async (ag: Agent) => {
    const path = ag.paused ? `/agents/${ag.id}/resume` : `/agents/${ag.id}/pause`
    await apiPost(path, {})
    setAgents(prev => prev.map(x => x.id === ag.id ? { ...x, paused: !ag.paused, state: !ag.paused ? 'paused' : x.state } : x))
  }

  const toggleMode = async (ag: Agent) => {
    const simulate = !ag.exec_simulate
    try {
      await apiPost(`/agents/${ag.id}/exec-mode`, { simulate })
      setAgents(prev => prev.map(x => x.id === ag.id ? { ...x, exec_simulate: simulate } : x))
      alert(t('agents.feedback.mode_updated'))
    } catch (e: any) {
      alert(e?.message || t('agents.errors.mode_update'))
    }
  }

  if (loading) return <div>{t('common.loading')}</div>
  if (error) return <div className="text-red-600">{error}</div>

  return (
    <div>
      <h1 className="text-xl font-semibold mb-4">Agents</h1>
      <div className="overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b dark:border-gray-700">
              <th className="py-2 pr-3">ID</th>
              <th className="py-2 pr-3">Endpoint</th>
              <th className="py-2 pr-3">{t('table.state')}</th>
              <th className="py-2 pr-3">{t('agents.table.mode')}</th>
              <th className="py-2 pr-3">Version</th>
              <th className="py-2 pr-3">OS/Arch</th>
              <th className="py-2 pr-3">{t('table.last')}</th>
              <th className="py-2 pr-3">{t('table.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(ag => (
              <tr key={ag.id} className="border-b dark:border-gray-800">
                <td className="py-2 pr-3">{ag.id}</td>
                <td className="py-2 pr-3">{ag.endpoint_id}</td>
                <td className="py-2 pr-3">{ag.paused ? t('agents.mode.paused') : ag.state}</td>
                <td className="py-2 pr-3">{ag.exec_simulate ? t('agents.mode.simulate') : t('agents.mode.real')}</td>
                <td className="py-2 pr-3">{ag.version || '-'}</td>
                <td className="py-2 pr-3">{ag.os}/{ag.arch}</td>
                <td className="py-2 pr-3">{ag.last_seen ? new Date(ag.last_seen).toLocaleString() : '-'}</td>
                <td className="py-2 pr-3 flex gap-2 flex-wrap">
                  <button className="btn-secondary" onClick={() => togglePause(ag)}>{ag.paused ? t('agents.actions.resume') : t('agents.actions.pause')}</button>
                  <button className="btn-secondary" onClick={() => toggleMode(ag)}>{ag.exec_simulate ? t('agents.actions.set_real') : t('agents.actions.set_simulate')}</button>
                  <button className="btn" onClick={() => genInstaller(ag.endpoint_id)}>{t('agents.actions.installer')}</button>
                  <button className="btn" onClick={() => genDocker(ag.endpoint_id)}>Docker</button>
                  <button className="btn-secondary" onClick={() => rotateKey(ag)}>{t('agents.actions.rotate')}</button>
                  <button className="btn-secondary" onClick={() => deleteAgent(ag)}>{t('agents.actions.delete')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

